<script setup lang="ts">
import { computed, Ref, ref } from "vue";
import {
  IonContent,
  IonRow,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonButtons,
  IonToggle,
} from "@ionic/vue";
import { useI18n } from "@/composables/useI18n";
import { format, parseISO, addDays, addBusinessDays } from "date-fns";
import { useFabriqStore } from "@/store/fabriq";
import { getNextMonday } from "@/utils/dates";

const props = withDefaults(
  defineProps<{
    date?: string | null;
    end?: string | null;
    allowRange?: boolean;
  }>(),
  {
    date: format(new Date(), "yyyy-MM-dd"),
    end: null,
    allowRange: false,
  }
);

const emit = defineEmits<{
  (
    e: "done",
    newDate: null | { start: string | null; end: string | null }
  ): void;
  (e: "cancel"): void;
}>();

const { t } = useI18n();

function getInitialStartDate() {
  if (props.date) return parseISO(props.date);
  return null;
}
function getInitialEndDate() {
  if (props.end) return parseISO(props.end);
  if (props.date) return addBusinessDays(parseISO(props.date), 1);
  return null;
}

const { theme, colorScheme } = useFabriqStore();
const today = new Date();
const rangePicker = ref();
const tomorrow = addDays(today, 1);
const monday = parseISO(getNextMonday());
const model: Ref<Date | null> = ref(props.date ? parseISO(props.date) : null);
const range: Ref<{ start: Date | null; end: Date | null }> = ref({
  start: getInitialStartDate(),
  end: getInitialEndDate(),
});
const setStart = ref(true);
const modified = ref(false);
const forceRange = ref<boolean>((props.end?.length ?? 0) > 0);

const done = (ev: any) => {
  if (!isRange.value) {
    emit("done", {
      start: format(ev, "yyyy-MM-dd"),
      end: null,
    });
  } else {
    emit("done", {
      start: format(ev.start, "yyyy-MM-dd"),
      end: format(ev.end, "yyyy-MM-dd"),
    });
  }
};

const rangeIsDone = () => {
  if (range.value?.start && range.value?.end && setStart.value) {
    done(range.value);
  }
};

const setDate = (date: Date) => {
  if (!isRange.value) return done(date);
  if (setStart.value) {
    range.value = { start: date, end: date };
    setStart.value = false;
  } else {
    range.value.end = date;
    if (range.value.start && range.value.end < range.value.start) {
      range.value = { start: date, end: range.value.start };
    }
    done(range.value);
  }
  rangePicker.value.forceUpdateValue(range.value);
};

const isDark = computed(() => {
  if (!theme) return colorScheme === "dark";
  return theme === "dark";
});

const isRange = computed(() => forceRange.value);

const changeIsRange = (wantRange: boolean) => {
  forceRange.value = wantRange;
  range.value = { start: null, end: null };
  model.value = null;
};
</script>
<template>
  <ion-header mode="ios">
    <ion-toolbar class="transparent-toolbar edit-modal-toolbar">
      <div slot="start">
        <ion-button fill="clear" @click="emit('done', null)">
          {{ t("common.clearDate") }}
        </ion-button>
      </div>
      <ion-title></ion-title>
      <ion-buttons slot="end" class="buttons-done-cancel">
        <ion-button
          v-if="modified"
          fill="clear"
          class="cancel-btn"
          @click="emit('cancel')"
        >
          {{ t("common.done") }}
        </ion-button>
        <ion-button
          v-else
          fill="clear"
          class="cancel-btn"
          @click="emit('cancel')"
        >
          {{ t("common.cancel") }}
        </ion-button>
      </ion-buttons>
    </ion-toolbar>
  </ion-header>
  <ion-content class="normal">
    <ion-row class="ion-justify-content-center ion-align-items-start">
      <v-date-picker
        :is-dark="isDark"
        is-range
        is-expanded
        v-if="isRange"
        ref="rangePicker"
        v-model="range"
        @update:modelValue="rangeIsDone"
      />
      <v-date-picker
        :is-dark="isDark"
        is-expanded
        v-else
        v-model="model"
        @update:modelValue="done"
      />
    </ion-row>
    <ion-row
      v-if="allowRange"
      @click="changeIsRange(!isRange)"
      class="ion-margin ion-justify-content-between ion-align-items-center"
    >
      <div class="big-button">{{ t("dates.endDate") }}</div>
      <ion-toggle
        size="small"
        class="toggle-btn"
        :checked="isRange"
        color="secondary"
      />
    </ion-row>
    <ion-row
      class="ion-margin date-buttons-container ion-justify-content-between ion-align-items-center"
    >
      <ion-button class="date-buttons" fill="outline" @click="setDate(today)">
        {{ t("datetime.today") }}
      </ion-button>
      <ion-button
        class="date-buttons"
        fill="outline"
        @click="setDate(tomorrow)"
      >
        {{ t("datetime.tomorrow") }}
      </ion-button>
      <ion-button class="date-buttons" fill="outline" @click="setDate(monday)">
        {{ t("datetime.monday") }}
      </ion-button>
    </ion-row>
  </ion-content>
</template>

<style scoped>
.date-buttons-container {
  width: calc(100% - var(--ion-padding) * 2.5);
  gap: var(--ion-padding);
}
.date-buttons {
  --border-color: var(--ion-border-color);
  --color: var(--ion-color-primary);
  flex: 1;
}
ion-toggle {
  --track-background-checked: var(--ion-color-secondary);
  --track-background: var(--ion-color-primary-shade);
  width: 40px;
  height: 20px;
  --handle-width: 16px;
  --handle-height: 16px;
}
.vc-container {
  border: 0;
}
.buttons-done-cancel {
  margin-right: var(--ion-padding);
}
</style>
<style>
.vc-day {
  margin-top: 8px;
}
</style>
